import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Box,
    Card,
    Chip,
    Container,
    Divider,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import Page from "../../Page";

const COUNTDOWN_SEC = 10;

/**
 * HdfcPaymentReceipt
 *
 * Shown inside the HDFC popup window after payment.
 * - Reads transaction details from URL query params forwarded by the backend.
 * - Counts down, then redirects the parent window to /payment/status and closes
 *   itself — exactly the same behaviour as the old PaymentResponse flow, but with
 *   a proper receipt UI.
 *
 * Query params expected:
 *   status        – "paid" | anything else → treated as failed
 *   referenceId   – merchantTransactionID / order_id
 *   amount        – raw amount value
 *   currency      – e.g. "INR"
 *   mode          – payment_method_type
 *   dateTime      – ISO date string or human-readable from backend
 *   user_pk       – extracted from order_id by backend
 *   parentUrl     – full parent window redirect URL (encoded), built by backend
 */
export const HdfcPaymentReceipt = () => {
    const theme = useTheme();
    const [searchParams] = useSearchParams();
    const timerRef = useRef(null);

    // ── Read params ────────────────────────────────────────────────────────────
    const status = searchParams.get("status") ?? "";
    const isSuccess = status === "paid";

    const referenceId = searchParams.get("referenceId") ?? "N/A";
    const rawAmount = searchParams.get("amount");
    const currency = searchParams.get("currency") ?? "INR";
    const mode = searchParams.get("mode") ?? "N/A";
    const dateTimeRaw = searchParams.get("dateTime");
    // parentUrl is kept in query params by the backend but navigation is
    // handled by the parent window's checkInterval → verifyHdfcPayment flow.

    // Format amount
    const displayAmount = (() => {
        if (!rawAmount) return "N/A";
        const num = parseFloat(rawAmount);
        if (isNaN(num)) return rawAmount;
        return `${currency} ${num.toFixed(2)}`;
    })();

    // Format date/time
    const dateLocaleOpts = {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    };
    const displayDateTime = (() => {
        if (!dateTimeRaw) return new Date().toLocaleString("en-IN", dateLocaleOpts);
        try {
            return new Date(dateTimeRaw).toLocaleString("en-IN", dateLocaleOpts);
        } catch {
            return dateTimeRaw;
        }
    })();

    // ── Countdown + auto-close ─────────────────────────────────────────────────
    const [remaining, setRemaining] = useState(COUNTDOWN_SEC);

    // ── Countdown + auto-close ─────────────────────────────────────────────────
    // IMPORTANT: Do NOT navigate window.opener here.
    // The parent window's checkInterval (in hdfc.js) detects popup.closed and
    // calls verifyHdfcPayment → submitHandler. Navigating the parent window here
    // would destroy that JS context before verifyHdfcPayment can run.
    useEffect(() => {
        timerRef.current = setInterval(() => {
            setRemaining((prev) => {
                const next = prev - 1;
                if (next <= 0) {
                    clearInterval(timerRef.current);
                    // Just close the popup — the parent's checkInterval handles the rest
                    window.close();
                }
                return next;
            });
        }, 1000);

        return () => clearInterval(timerRef.current);
    }, []);

    // ── Colours ────────────────────────────────────────────────────────────────
    const successColor = theme.palette.success?.main ?? "#4caf50";
    const errorColor = theme.palette.error?.main ?? "#f44336";
    const accentColor = isSuccess ? successColor : errorColor;

    // ── Detail rows ────────────────────────────────────────────────────────────
    const rows = [
        { label: "Amount", value: displayAmount, highlight: true },
        { label: "Order ID", value: referenceId },
        { label: "Date", value: displayDateTime },
        { label: "Payment Mode", value: mode },
        {
            label: "Status",
            value: isSuccess ? "SUCCESS" : status.toUpperCase() || "FAILED",
            chip: true,
        },
    ];

    return (
        <Page
            title={isSuccess ? "Payment Successful | HDFC" : "Payment Failed | HDFC"}
            sx={{ minHeight: "100vh", display: "flex", alignItems: "center" }}
        >
            <Container maxWidth="xs" sx={{ py: 4 }}>
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: "hidden",
                    }}
                >
                    {/* Coloured top stripe */}
                    <Box sx={{ height: 5, bgcolor: accentColor }} />

                    <Box sx={{ p: { xs: 3, sm: 4 } }}>
                        {/* ── Status icon + heading ── */}
                        <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                            {isSuccess ? (
                                <CheckCircleOutlineRoundedIcon
                                    sx={{
                                        fontSize: 64,
                                        color: successColor,
                                        filter: `drop-shadow(0 0 8px ${successColor}55)`,
                                    }}
                                />
                            ) : (
                                <CancelOutlinedIcon
                                    sx={{
                                        fontSize: 64,
                                        color: errorColor,
                                        filter: `drop-shadow(0 0 8px ${errorColor}55)`,
                                    }}
                                />
                            )}

                            <Typography
                                variant="h6"
                                fontWeight={700}
                                color={accentColor}
                                textAlign="center"
                            >
                                {isSuccess ? "Payment Successful!" : "Payment Failed"}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                textAlign="center"
                            >
                                {isSuccess
                                    ? "Your transaction was completed successfully."
                                    : "We could not process your payment. Please try again."}
                            </Typography>
                        </Stack>

                        <Divider sx={{ mb: 2.5 }} />

                        {/* ── Transaction details ── */}
                        <Stack spacing={0} sx={{ mb: 3 }}>
                            {rows.map(({ label, value, highlight, chip }, idx) => (
                                <Box
                                    key={label}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: 2,
                                        py: 1.25,
                                        px: 0.5,
                                        borderBottom:
                                            idx < rows.length - 1
                                                ? `1px solid ${theme.palette.divider}`
                                                : "none",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight={500}
                                        sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                                    >
                                        {label}
                                    </Typography>

                                    {chip ? (
                                        <Chip
                                            label={value}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "0.72rem",
                                                bgcolor: isSuccess
                                                    ? `${successColor}18`
                                                    : `${errorColor}18`,
                                                color: accentColor,
                                                border: `1px solid ${accentColor}55`,
                                            }}
                                        />
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            fontWeight={highlight ? 700 : 600}
                                            color={highlight ? accentColor : "text.primary"}
                                            textAlign="right"
                                            sx={{ wordBreak: "break-all" }}
                                        >
                                            {value}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Stack>

                        <Divider sx={{ mb: 2.5 }} />

                        {/* ── Countdown ── */}
                        <Stack spacing={1}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                textAlign="center"
                            >
                                This window will close automatically in{" "}
                                <Box
                                    component="span"
                                    sx={{ fontWeight: 700, color: accentColor }}
                                >
                                    {remaining}s
                                </Box>
                            </Typography>

                            {/* Smooth CSS-animated bar — runs at native frame rate, no JS steps */}
                            <Box
                                sx={{
                                    height: 4,
                                    borderRadius: 2,
                                    bgcolor: `${accentColor}22`,
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        height: "100%",
                                        width: "100%",
                                        borderRadius: 2,
                                        bgcolor: accentColor,
                                        transformOrigin: "left center",
                                        animation: `hdfcCountdown ${COUNTDOWN_SEC}s linear forwards`,
                                        "@keyframes hdfcCountdown": {
                                            from: { transform: "scaleX(1)" },
                                            to: { transform: "scaleX(0)" },
                                        },
                                    }}
                                />
                            </Box>
                        </Stack>
                    </Box>
                </Card>
            </Container>
        </Page>
    );
};
